import { coordinateToChartTime } from '../../../lib/chart/chartTimeProjection.js'
import { snapTrendlinePoint } from './trendlineSnap.js'
// Drag existing drawings through public pane/time/price APIs. Preview locally;
// commit exactly once on release so persistence and undo remain atomic.
export function installDrawingDrag({ api, overlay, getState, onSelect, onPreview, onSettings, suppressClicks }) {
  const pane = api.chart.panes?.()[api.paneIndex ?? 0], element = pane?.getHTMLElement()
  if (!element) return () => {}
  let drag = null, lastClick = null, lastPointer = null
  const position = event => {
    const box = element.getBoundingClientRect()
    return { x: event.clientX - box.left - api.chart.priceScale('left').width(), y: event.clientY - box.top }
  }
  const finish = (commit, event) => {
    if (!drag) return
    const previous = drag; drag = null; lastPointer = null
    try { api.chart.applyOptions(previous.options) } catch { /* disposed */ }
    if (element.hasPointerCapture?.(previous.pointerId)) element.releasePointerCapture(previous.pointerId)
    onPreview(null)
    suppressClicks()
    if (commit && previous.moved && previous.next) {
      const state = getState()
      state.commit(state.items.map(item => item.id === previous.item.id ? previous.next : item))
      lastClick = null
    } else if (commit && !previous.moved) {
      const now = Date.now()
      if (lastClick?.id === previous.item.id && now - lastClick.time < 450 && Math.hypot(previous.start.x - lastClick.x, previous.start.y - lastClick.y) < 6) { onSettings(previous.item.id); lastClick = null }
      else lastClick = { id: previous.item.id, time: now, ...previous.start }
    }
    event?.preventDefault(); event?.stopImmediatePropagation()
  }
  const down = event => {
    const state = getState()
    if (event.metaKey || event.shiftKey || event.button !== 0 || state.tool !== 'select' || !state.ready || state.draft) return
    const point = position(event), id = overlay.pick(point.x, point.y), item = state.items.find(item => item.id === id)
    if (!item || item.kind === 'pen') return
    const anchors = item.points.map(p => overlay.project(p))
    if (anchors.some(p => !p)) return
    const handles = item.kind === 'rectangle' ? [...anchors, { x: anchors[0].x, y: anchors[1].y }, { x: anchors[1].x, y: anchors[0].y }] : anchors
    const endpoint = handles.findIndex(p => Math.hypot(p.x - point.x, p.y - point.y) < 10)
    lastPointer = null
    drag = { item, anchors, endpoint, start: point, pointerId: event.pointerId, moved: false,
      options: structuredClone({ handleScroll: api.chart.options().handleScroll, handleScale: api.chart.options().handleScale }) }
    api.chart.applyOptions({ handleScroll: false, handleScale: false })
    state.onActivate(); onSelect(id); element.setPointerCapture?.(event.pointerId)
    event.preventDefault(); event.stopImmediatePropagation(); suppressClicks()
  }
  const move = event => {
    if (!drag || event.pointerId !== drag.pointerId) return
    lastPointer = event
    const point = position(event), dx = point.x - drag.start.x, dy = point.y - drag.start.y
    if (Math.hypot(dx, dy) < 3 && !drag.moved) return
    drag.moved = true
    const points = drag.item.points.map((original, index) => {
      if (drag.item.kind === 'rectangle' && drag.endpoint >= 2) {
        const timeIndex = drag.endpoint === 2 ? 0 : 1, priceIndex = 1 - timeIndex
        const time = index === timeIndex ? coordinateToChartTime(api.chart, api.series, drag.anchors[index].x + dx, api.timeframe) : original.time
        const price = index === priceIndex ? api.series.coordinateToPrice(drag.anchors[index].y + dy) : original.price
        return Number.isFinite(time) && Number.isFinite(price) ? { time, price } : null
      }
      if (drag.endpoint >= 0 && drag.endpoint !== index) return original
      const anchor = drag.anchors[index]
      if (drag.item.kind === 'line' && drag.endpoint >= 0 && event.shiftKey) return snapTrendlinePoint(api, drag.item.points[1 - index], { x: anchor.x + dx, y: anchor.y + dy })
      const time = coordinateToChartTime(api.chart, api.series, anchor.x + dx, api.timeframe), price = api.series.coordinateToPrice(anchor.y + dy)
      return Number.isFinite(time) && Number.isFinite(price) ? { time, price } : null
    })
    if (points.every(Boolean)) { drag.next = { ...drag.item, points }; onPreview(drag.next) }
    event.preventDefault(); event.stopImmediatePropagation()
  }
  const up = event => { if (drag?.pointerId === event.pointerId) finish(true, event) }
  const cancel = event => { if (drag?.pointerId === event.pointerId) finish(false, event) }
  const blur = () => finish(false)
  const key = event => {
    if (drag && event.key === 'Escape') finish(false, event)
    if (drag && lastPointer && event.key === 'Shift') move({ pointerId: lastPointer.pointerId, clientX: lastPointer.clientX, clientY: lastPointer.clientY, shiftKey: event.type === 'keydown', preventDefault() {}, stopImmediatePropagation() {} })
  }
  element.addEventListener('pointerdown', down, true)
  element.addEventListener('pointermove', move, true)
  element.addEventListener('pointerup', up, true)
  element.addEventListener('pointercancel', cancel, true)
  element.addEventListener('lostpointercapture', cancel, true)
  window.addEventListener('blur', blur)
  window.addEventListener('keydown', key, true)
  window.addEventListener('keyup', key, true)
  return () => {
    finish(false)
    element.removeEventListener('pointerdown', down, true); element.removeEventListener('pointermove', move, true)
    element.removeEventListener('pointerup', up, true); element.removeEventListener('pointercancel', cancel, true)
    element.removeEventListener('lostpointercapture', cancel, true); window.removeEventListener('blur', blur)
    window.removeEventListener('keydown', key, true)
    window.removeEventListener('keyup', key, true)
  }
}
